/**
 * Structured error description for API failures.
 *
 * The Detailed-Description Workbench showed a bare "Request failed."
 * for a cost cap, an edge timeout and a provider outage alike, because
 * its inline extractor read exactly one shape:
 *
 *     const d = e?.response?.data?.detail;
 *     setErr((typeof d === 'object' ? d?.detail : d) || 'Request failed.');
 *
 * FastAPI's `detail` is a string on some routes, an object with
 * `{error_code, message, ...}` on the spec routes, and an array of
 * `{loc, msg}` on a 422. The spec cost cap deliberately builds
 * `{error_code, window_hours, reset_at, message}` with a "wait or
 * contact an admin" message — and `d.detail` on that object is
 * `undefined`, so the carefully written copy was thrown away and
 * replaced with the generic string. A 524 from the CDN edge (a long
 * enable running past the ~100s proxy timeout) has no JSON body at
 * all and landed in the same place.
 *
 * This returns the pieces a banner needs: a human message, a
 * machine-readable code, the HTTP status when there was one, and a
 * coarse `kind` so the UI can pick copy and an affordance (retry vs
 * wait vs sign in) without re-sniffing shapes.
 *
 * Free text still goes through `sanitizeApiError`, so backend
 * internals never reach the UI verbatim.
 */
import { sanitizeApiError } from './sanitizeApiError';

export type ApiErrorKind =
  | 'cost_cap'
  | 'timeout'
  | 'network'
  | 'provider'
  | 'validation'
  | 'not_found'
  | 'conflict'
  | 'permission'
  | 'unknown';

export interface DescribedApiError {
  /** Human-readable, already sanitised. Never empty. */
  message: string;
  /** Backend `error_code` when one was supplied. */
  code?: string;
  /** HTTP status when the request reached the server. */
  status?: number;
  kind: ApiErrorKind;
  /** True when retrying the same request could plausibly work. */
  retryable: boolean;
  /** True when nothing more specific than "the request failed" is
   *  known, so a call site's own wording describes it better. */
  generic?: boolean;
}

const GENERIC = 'Request failed.';

function firstString(...vals: unknown[]): string | undefined {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return undefined;
}

/** FastAPI 422: `detail` is an array of `{loc, msg, type}`. */
function fromValidationArray(detail: unknown[]): string | undefined {
  const parts = detail
    .map((d) => {
      if (!d || typeof d !== 'object') return null;
      const rec = d as Record<string, unknown>;
      const msg = typeof rec.msg === 'string' ? rec.msg : null;
      if (!msg) return null;
      const loc = Array.isArray(rec.loc)
        ? rec.loc.filter((p) => typeof p === 'string' && p !== 'body').join('.')
        : '';
      return loc ? `${loc}: ${msg}` : msg;
    })
    .filter(Boolean) as string[];
  return parts.length ? parts.slice(0, 3).join('; ') : undefined;
}

function kindFor(status: number | undefined, code: string | undefined):
ApiErrorKind {
  if (code === 'spec_cost_cap_exceeded' || status === 429) return 'cost_cap';
  if (status === 504 || status === 524 || status === 408) return 'timeout';
  if (status === 502 || status === 503) return 'provider';
  if (status === 422 || status === 400 || status === 413) return 'validation';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 401 || status === 403) return 'permission';
  return 'unknown';
}

export function describeApiError(err: unknown): DescribedApiError {
  const anyErr = err as any;
  const response = anyErr?.response;
  const status: number | undefined =
    typeof response?.status === 'number' ? response.status : undefined;

  // No response at all: the request never completed. An enable that
  // runs past the CDN's proxy timeout arrives here (the socket is cut
  // before any body), which is why "Request failed." was the most
  // common thing a drafter saw.
  if (!response) {
    const raw = firstString(anyErr?.message);
    const isAbort =
      anyErr?.code === 'ECONNABORTED'
      || anyErr?.name === 'AbortError'
      || (raw ? /timeout/i.test(raw) : false);
    if (isAbort) {
      return {
        message: 'The request timed out before the server answered. '
          + 'Long generations can outlive the connection — reload to '
          + 'see whether the work completed.',
        kind: 'timeout',
        retryable: true,
      };
    }
    // Only axios's transport failures are genuinely "can't reach the
    // server". An Error thrown by our own code (a job that reported
    // failed, a guard that rejected) carries a real message, and
    // burying it under "check your connection" is the same class of
    // lie this module exists to remove.
    const isTransport =
      anyErr?.isAxiosError === true
      || !raw
      || /network error|failed to fetch|econnrefused|enotfound/i.test(raw);
    if (!isTransport) {
      return {
        message: sanitizeApiError(raw),
        kind: 'unknown',
        retryable: false,
      };
    }
    return {
      message: 'Could not reach the server. Check your connection and '
        + 'try again.',
      kind: 'network',
      retryable: true,
    };
  }

  const data = response.data;
  const detail = data?.detail;

  let code: string | undefined;
  let message: string | undefined;
  let generic = false;

  if (typeof detail === 'string') {
    message = detail;
  } else if (Array.isArray(detail)) {
    message = fromValidationArray(detail);
  } else if (detail && typeof detail === 'object') {
    const rec = detail as Record<string, unknown>;
    code = typeof rec.error_code === 'string' ? rec.error_code : undefined;
    // `message` is the spec module's field; `detail` is the nested
    // string some older routes use; `msg` covers hand-rolled shapes.
    message = firstString(rec.message, rec.detail, rec.msg);
  }
  // A CDN timeout (Cloudflare's 524) arrives as an HTML error page.
  // Adopting it would sanitise to the generic "An error occurred"
  // copy and hide the timeout wording below.
  const htmlTimeout = (status === 504 || status === 524 || status === 408)
    && typeof data === 'string' && /^\s*</.test(data);
  if (!message && typeof data === 'string' && !htmlTimeout) {
    // Starlette's plain-text 500 — an unwrapped exception.
    message = data;
  }
  if (!message) {
    // Body-level message only. Deliberately NOT `anyErr.message`:
    // once a response exists, axios sets that to "Request failed with
    // status code N", which is how a 524 edge timeout ended up
    // indistinguishable from everything else. The kind-specific
    // fallbacks below say something true instead.
    message = firstString(data?.message);
  }

  const kind = kindFor(status, code);

  // Status-specific fallbacks so a bodyless response still says
  // something true rather than "Request failed."
  if (!message) {
    if (kind === 'cost_cap') {
      message = 'This matter has reached its AI spend cap for now.';
    } else if (kind === 'timeout') {
      message = 'The server took too long to answer.';
    } else if (kind === 'provider') {
      message = 'The AI provider is unavailable right now.';
    } else if (kind === 'permission') {
      message = 'You do not have permission to do that.';
    } else if (status) {
      message = `The server returned an error (HTTP ${status}).`;
      generic = true;
    }
  }

  return {
    message: message ? sanitizeApiError(message) : GENERIC,
    code,
    status,
    kind,
    retryable:
      kind === 'timeout' || kind === 'network' || kind === 'provider',
    generic: generic || !message,
  };
}

/** Convenience for call sites that only render a string. */
export function apiErrorMessage(err: unknown): string {
  return describeApiError(err).message;
}

/**
 * The message to put in an error banner: the server's own explanation
 * when it gave one, otherwise the call site's `fallback`.
 *
 * Replaces the inline `e.response?.data?.detail || 'Save failed'`. That
 * passed the server's `{error_code, message, ...}` object straight into
 * React state, and rendering it threw "Objects are not valid as a React
 * child" — Stage 3's Run QC took the whole matter view down that way on
 * a plain 422. This always returns a string.
 */
export function apiErrorText(err: unknown, fallback: string): string {
  const d = describeApiError(err);
  return d.generic ? fallback : d.message;
}
